import { equals, all, any, pathOr, map, filter, includes, not, head } from "ramda";
import { TRUE } from "src/constants";
import { canGuard } from "src/features/RBAC/Can";
import { splitTheSavedDataPathWithDot } from "src/features/core/ChatEngine/Configs/autoAnswerConfigurator";
import { mergeWithConcat } from "src/utilities/arrayObjects";
import { returnIsNotEmptyOrTrue, isEmptyOrNil, isNotEmpty } from "src/utilities/hasValue";


interface GeneralRule {
    conditions: {
        apply_logic: 'and' | 'any';
        stage: {
            apply_logic: 'and' | 'any';
            rules: {
                match: string;
                not_match: string;
                in_history: string;
            }[]
        };
        permission: {
            apply_logic: 'and' | 'any';
            rules: {
                match: string;
                not_match: string;
            }[]
        }
    }
}

export class SingleRuleApply {
    rules: GeneralRule;
    sitePackage: {
        // loanProducts: any;
        permissions: any;
    };
    constructor(sitePackage: any, rules: GeneralRule) {
        this.rules = rules
        this.sitePackage = sitePackage
    }



    getAppyFn(apply_logic: 'and' | 'any') {
        //TO-DO:This needs to be fixed in config to allow only and
        return returnIsNotEmptyOrTrue(
            includes(apply_logic, ['and','all']),
            all,
            any
        )
    }

    permissionMatch() {
        const { permissions } = this.sitePackage;
        const rules = pathOr([], ['conditions', 'permission', 'rules'], this.rules)
        if (isEmptyOrNil(rules)) {
            return false
        }

        const results = map((rule: any) => {
            const match = pathOr('', ['match'], rule)
            const notMatch = pathOr('', ['not_match'], rule)
            const hasPermissionMatch = isNotEmpty(match) && canGuard(permissions, match)
            const hasPermissionNotMatch = isNotEmpty(notMatch) && !canGuard(permissions, match)
            return hasPermissionMatch || hasPermissionNotMatch;
        }, rules)


        const applyFn = this.getAppyFn(
            pathOr('any', ['conditions', 'permission', 'apply_logic'], this.rules)
        )

        return applyFn(equals(true), results)
    }

    stageMatch(loanStatus: any) {
        const rules = pathOr([], ['conditions', 'stage', 'rules'], this.rules)
        if (isEmptyOrNil(rules)) {
            return false
        }
        const phase = pathOr('', ['status', 'phase'], loanStatus)
        const conditions = pathOr([], ['status', 'conditions'], loanStatus)

        const results = map((rule: any) => {
            const match = pathOr('', ['match'], rule)
            const notMatch = pathOr('', ['not_match'], rule)
            const history = pathOr('', ['in_history'], rule)
            const notInhistory = pathOr('', ['not_in_history'], rule)

            const hasStageMatch = isNotEmpty(match) && equals(phase, match)
            const hasStageNotMatch = isNotEmpty(notMatch) && !equals(phase, notMatch)
            const hasInHistory = isNotEmpty(history) && isNotEmpty(filter((item: any) => equals(head(splitTheSavedDataPathWithDot(pathOr('', ['condition_type'], item))), history), conditions))
            const hasNotInHistory = isNotEmpty(notInhistory) && isEmptyOrNil(filter((item: any) => equals(head(splitTheSavedDataPathWithDot(pathOr('', ['condition_type'], item))), notInhistory), conditions))

            return hasStageMatch || hasStageNotMatch || hasInHistory || hasNotInHistory;
        }, rules)
        const applyFn = this.getAppyFn(
            pathOr('any', ['conditions', 'stage', 'apply_logic'], this.rules)
        )
        return applyFn(equals(true), results)
    }

    private getResult(loanApp: any) {
        const permissionMatch = this.permissionMatch();
        const stageMatch = this.stageMatch(pathOr({}, ['spec', 'loan_status', 0], loanApp));
        const result = [permissionMatch, stageMatch]
        return  result
    }

    private _hasSatisfies(loanApp: any) {
        const applyFn = this.getAppyFn(
            pathOr('any', ['conditions', 'apply_logic'], this.rules)
        )
        const results = this.getResult(loanApp)
        return applyFn(equals(true), results)
    }

    // exposed only to do unit test 
    hasSatisfies(loanApp: any) {
        return this._hasSatisfies(loanApp)
    }

    apply(loanApp: any, pageSections: string[]) {
        const defaultDisable = { displayerNames: [], groupNames: [], excludeDisplayers: [] }
        if (!this._hasSatisfies(loanApp)) {
            return defaultDisable
        }
        const shouldDisableWholePage = equals(pathOr('', ['apply_on_page'], this.rules), TRUE);
        const excludeGroups = pathOr([], ['excludes', 'groups'], this.rules)

        return {
            displayerNames: returnIsNotEmptyOrTrue(
                shouldDisableWholePage,
                [],
                pathOr([], ['page_displayers'], this.rules)
            ),
            groupNames: returnIsNotEmptyOrTrue(
                shouldDisableWholePage,
                filter((s: string) => not(includes(s, excludeGroups)), pageSections),
                pathOr([], ['page_groups'], this.rules)
            ),
            excludeDisplayers: pathOr([], ['excludes', 'displayers'], this.rules)
        }
    }

}

// TOOD: first step we disable or enable based on permissions and stages only.
class GeneralRuleApply {
    rules: SingleRuleApply[];
    tabConfig: any;
    sitePackage: {
        // loanProducts: any;
        permissions: any;
    };
    constructor(sitePackage: any, tabConfig: any) {
        this.tabConfig = tabConfig
        const rules: GeneralRule[] = pathOr([], ['disable_when'], tabConfig)
        this.rules = map((rule: GeneralRule) => new SingleRuleApply(sitePackage, rule), rules)
        this.sitePackage = sitePackage
    }

    apply(loanApp: any, pageSections: string[]) {
        const disabledConfigs = mergeWithConcat(map((rule: SingleRuleApply) => rule.apply(loanApp, pageSections), this.rules))
        const groupNames = pathOr([], ['groupNames'], disabledConfigs)
        const excludeDisplayers = pathOr([], ['excludeDisplayers'], disabledConfigs)
        const displayerNames = pathOr([], ['displayerNames'], disabledConfigs)
        return {
            groupNames,
            excludeDisplayers,
            displayerNames
        }
    }
}

export {
    GeneralRuleApply
}